// Work-in-progress React component for managing hotel bookings
// Similar layout and logic to ManageRegistrations
// Includes booking-level and guest-level details

import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  message,
  DatePicker,
  Modal,
  Checkbox,
} from "antd";
import { db, collection } from "../../firebaseConfig";
import { getDocs, orderBy, query, doc, updateDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import { debounce } from "lodash";
import { FaFileExcel } from "react-icons/fa";
import { ROOM_OPTIONS } from "../booking/Step1";

const { TextArea } = Input;

export interface Guest {
  docId?: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  primary?: boolean;
  companionName?: string;
  companionRelationship?: string;
  occupancy?: "Single" | "Double";
}

export interface HotelBooking {
  key: string;
  bookingReference: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  roomName: string;
  occupancy: string;
  rooms: number;
  totalPrice: number;
  status?: string;
  notes?: string;
  guests: Guest[];
  timestamp: string;
}

export const formatDate = (date: Date) =>
  date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const ManageHotelBookings: React.FC = () => {
  const [bookings, setBookings] = useState<HotelBooking[]>([]);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    const bookingsRef = collection(db, "hotelBookings");
    const q = query(bookingsRef, orderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);

    const results: HotelBooking[] = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const guestsRef = collection(db, `hotelBookings/${docSnap.id}/guests`);
        const guestsSnap = await getDocs(guestsRef);

        const guests: Guest[] = guestsSnap.docs.map((g) => ({
          docId: g.id,
          title: g.data().title,
          firstName: g.data().firstName,
          lastName: g.data().lastName,
          email: g.data().email,
          mobile: g.data().mobile,
          primary: g.data().primary,
          companionName: g.data().companion?.fullName,
          companionRelationship: g.data().companion?.relationship,
          occupancy: g.data().occupancy,
        }));

        return {
          key: docSnap.id,
          bookingReference: data.reference,
          checkIn: data.checkIn,
          checkOut: data.checkOut,
          nights: data.nights,
          roomName: data.roomName,
          occupancy: data.occupancy,
          rooms: data.rooms,
          totalPrice: data.totalPrice,
          status: data.status || "Pending",
          notes: data.notes || "",
          guests,
          timestamp: data.timestamp.toLocaleString(),
        };
      })
    );

    setBookings(results);
  };

  const updateBookingField = async (
    bookingId: string,
    field: string,
    value: any
  ) => {
    const bookingRef = doc(db, `hotelBookings/${bookingId}`);
    try {
      await updateDoc(bookingRef, { [field]: value });
      message.success(`${field === "status" ? "Status" : "Notes"} updated.`);
    } catch (error) {
      console.error(error);
      message.error("Failed to update field.");
    }
  };

  const debounceStatus = debounce(updateBookingField, 400);
  const debounceNotes = debounce(updateBookingField, 500);

  const BCC_EMAILS = [
    "webmastercreativespace@gmail.com",
    "relhage@ica-arab.com",
    "accommodation@uaefma.com",
    "juna.m@uaefma.com",
    "saquib@uaefma.com",
    "icadxb@altayer.com",
  ];

  const handleResendEmail = async (booking: HotelBooking) => {
    const primary = booking.guests.find((g) => g.primary);
    if (!primary) return message.error("No primary guest found.");

    const checkInDate = new Date(booking.checkIn);
    const checkOutDate = new Date(booking.checkOut);
    const nights =
      (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24);
    const selectedRoom = ROOM_OPTIONS.find((r) => r.name === booking.roomName);
    if (!selectedRoom) {
      return message.error("Room pricing not found for this booking.");
    }

    const roomBreakdown = booking.guests.map((g, index) => {
      const rate =
        g.occupancy === "Single" ? selectedRoom.single : selectedRoom.double;

      const amount = rate * nights;

      return {
        roomNumber: `${index + 1}`,
        occupancy: g.occupancy || "Single",
        rate: rate.toFixed(2),
        nights: `${nights}`,
        amount: amount.toFixed(2),
      };
    });

    const enrichedGuests = booking.guests.map((g, index) => {
      const companionFilled = g.companionName;
      const base = {
        title: g.title,
        firstName: g.firstName,
        lastName: g.lastName,
        email: g.email,
        mobile: g.mobile,
        isPrimary: !!g.primary,
        roomNumber: index + 1,
        roomTotalRooms: booking.guests.length,
      };

      return companionFilled
        ? {
            ...base,
            companion: {
              companionName: g.companionName || "",
              companionRelationship: g.companionRelationship || "",
            },
          }
        : base;
    });

    const totalNights =
      (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24);

    const allEmails = [
      primary.email,
      "webmastercreativespace@gmail.com",
      "relhage@ica-arab.com",
      "accommodation@uaefma.com",
      "juna.m@uaefma.com",
      "saquib@uaefma.com",
      "icadxb@altayer.com",
    ];

    let selected: string[] = [...allEmails];

    Modal.confirm({
      title: "Resend Hotel Confirmation",
      content: (
        <Checkbox.Group
          defaultValue={allEmails}
          onChange={(vals) => {
            selected = vals as string[];
          }}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {allEmails.map((email) => (
            <Checkbox key={email} value={email}>
              {email}
            </Checkbox>
          ))}
        </Checkbox.Group>
      ),
      okText: "Send",
      onOk: async () => {
        try {
          await fetch(
            "https://us-central1-icaconference-438ba.cloudfunctions.net/sendHotelConfirmationNoBCC",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                personalizations: [
                  {
                    to: selected.map((email) => ({ email })),
                    dynamic_template_data: {
                      fullName: `${primary.title} ${primary.firstName} ${primary.lastName}`,
                      email: primary.email,
                      mobile: primary.mobile,
                      checkIn: formatDate(checkInDate),
                      checkOut: formatDate(checkOutDate),
                      roomName: booking.roomName,
                      totalRooms: booking.rooms,
                      totalNights: `${totalNights} night${
                        totalNights > 1 ? "s" : ""
                      }`,
                      totalPrice: booking.totalPrice.toFixed(2),
                      bookingReference: booking.bookingReference,
                      guests: enrichedGuests,
                      roomBreakdown,
                      invoiceDate: new Date().toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }),
                    },
                  },
                ],
                template_id: "d-c4736ba558274a489f155777f0d65986",
                from: {
                  email: "registration@comms-dev.com",
                  name: "ICA Conference",
                },
              }),
            }
          );

          message.success("Confirmation email resent!");
        } catch (err) {
          console.error(err);
          message.error("Failed to resend email.");
        }
      },
    });
  };

  const columns = [
    {
      title: "Booking Ref",
      dataIndex: "bookingReference",
      key: "bookingReference",
      width: 200,
    },
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 220,
    },
    {
      title: "Room",
      dataIndex: "roomName",
      key: "roomName",
      width: 180,
    },
    {
      title: "Occupancy",
      dataIndex: "occupancy",
      key: "occupancy",
      width: 120,
    },
    {
      title: "Rooms",
      dataIndex: "rooms",
      key: "rooms",
      width: 100,
    },
    {
      title: "Guests",
      key: "guests",
      render: (_: any, record: HotelBooking) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {record.guests.map((guest, index) => (
            <div
              key={guest.email}
              style={{
                padding: 8,
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                backgroundColor: "#fafafa",
              }}
            >
              <div style={{ fontWeight: 500, fontSize: 14 }}>
                Room {index + 1} of {record.guests.length}: {guest.title}{" "}
                {guest.firstName} {guest.lastName}
                {guest.primary && (
                  <span
                    style={{
                      marginLeft: 8,
                      background: "#dcfce7",
                      color: "#166534",
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Primary
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#555" }}>{guest.email}</div>
              <div style={{ fontSize: 13, color: "#555" }}>{guest.mobile}</div>
              <div style={{ fontSize: 13, color: "#555" }}>
                Occupancy: <strong>{guest.occupancy || "Single"}</strong>
              </div>
              {guest.companionName && (
                <div style={{ fontSize: 13, color: "#555" }}>
                  👥 Companion: <strong>{guest.companionName}</strong> (
                  {guest.companionRelationship})
                </div>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Total (USD)",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      render: (value: number) => `$${value.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (_: any, record: HotelBooking) => (
        <Select
          value={record.status}
          onChange={(val) => debounceStatus(record.key, "status", val)}
          size="small"
          style={{ width: "100%" }}
        >
          <Select.Option value="Pending">Pending</Select.Option>
          <Select.Option value="Confirmed">Confirmed</Select.Option>
          <Select.Option value="Cancelled">Cancelled</Select.Option>
        </Select>
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 420,
      render: (_: any, record: HotelBooking) => (
        <TextArea
          rows={2}
          defaultValue={record.notes}
          onChange={(e) => debounceNotes(record.key, "notes", e.target.value)}
          placeholder="Add notes or remarks"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_: any, record: HotelBooking) => (
        <Button onClick={() => handleResendEmail(record)} type="link">
          Resend Email
        </Button>
      ),
    },
  ];

  const handleExport = () => {
    const rows = bookings.flatMap((b) =>
      b.guests.map((g) => ({
        BookingRef: b.bookingReference,
        CheckIn: b.checkIn,
        CheckOut: b.checkOut,
        Room: b.roomName,
        Occupancy: b.occupancy,
        Rooms: b.rooms,
        Guest: `${g.title} ${g.firstName} ${g.lastName}`,
        Email: g.email,
        Mobile: g.mobile,
        Primary: g.primary ? "Yes" : "No",
        Companion: g.companionName || "",
        Relationship: g.companionRelationship || "",
        Status: b.status,
        Notes: b.notes,
        TotalUSD: b.totalPrice,
      }))
    );
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HotelBookings");
    XLSX.writeFile(wb, `ICA_Hotel_Bookings_${Date.now()}.xlsx`);
  };

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (value: string) => {
    setSearchTerm(value.toLowerCase());
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{ padding: 24 }}>
      {/* <h2 style={{ marginBottom: 16 }}>Hotel Bookings</h2> */}
      {/* <Button
        type="primary"
        onClick={handleExport}
        style={{ marginBottom: 16 }}
      >
        Export to Excel
      </Button> */}
      <div
        style={{
          marginBottom: 16,
          justifyContent: "space-between",
          display: "flex",
        }}
      >
        <h1 className="title" style={{ color: "#2e3c52" }}>
          Hotel Bookings
        </h1>

        <Button type="primary" icon={<FaFileExcel />} onClick={handleExport}>
          Export to Excel
        </Button>
      </div>

      <Input
        placeholder="Search..."
        style={{ width: isMobile ? 350 : 800, height: 53, marginBottom: 25 }}
        allowClear
        onChange={(e) => handleSearch(e.target.value)}
      />

      <Table
        dataSource={bookings.filter((b) => {
          if (!searchTerm) return true;
          const combined = `
      ${b.bookingReference}
      ${b.roomName}
      ${b.occupancy}
      ${b.status}
      ${b.notes}
      ${b.guests.map(
        (g) =>
          `${g.firstName} ${g.lastName} ${g.email} ${g.mobile} ${g.companionName} ${g.companionRelationship}`
      )}
    `.toLowerCase();
          return combined.includes(searchTerm);
        })}
        columns={columns}
        pagination={{ pageSize: 8 }}
        rowKey="key"
        bordered
      />
    </div>
  );
};

export default ManageHotelBookings;
